import { assertNotEmpty } from '@festina-life/core'
import { Card, CardBody } from '@festina-life/ui/structure'
import { useCallback } from 'react'

import { useTemplateContext } from '../../context/templateContext'
import { useComparableField } from '../../context/useComparableField'
import type { SubTemplateFieldValue } from '../../types/templateModel'
import { stringifyComparableValue } from '../diffIndicators/helpers'
import {
  type SerialiseValueParams,
  ValueSetupDiffIndicators,
} from '../diffIndicators/ValueSetupDiffIndicators'
import { InheritanceIndicator } from '../inheritanceIndicator/InheritanceIndicator'
import { TemplateDocumentationTooltip } from '../templateDocumentationTooltip/TemplateDocumentationTooltip'
import { SubTemplatedFieldValueDocumentation } from '../templateFieldDocumentation/SubTemplatedFieldValueDocumentation'
import { processSubTemplatedFieldValueForSerialisation } from './fieldsHelpers'
import { SubTemplateFieldValueName } from './SubTemplateFieldValueName'
import { TemplateFieldAnchor } from './TemplateFieldAnchor'

type Props = {
  variant: SubTemplateFieldValue
}

/**
 *
 */
export const SubTemplatedFieldValueNameCell = ({ variant }: Props) => {
  const comparableField = useComparableField<SubTemplateFieldValue>(
    variant.path,
  )
  const { templateVersion } = useTemplateContext()

  assertNotEmpty(templateVersion)

  const valueFromComparable = useCallback(
    ({
      value: comparable,
      valueType,
    }: SerialiseValueParams<SubTemplateFieldValue>) => {
      if (!comparable?.subTemplatedField) {
        return ''
      }

      return stringifyComparableValue(
        processSubTemplatedFieldValueForSerialisation({
          value: comparable,
          valueType,
          currentValue: variant,
          currentValuePath: variant.path,
        }),
      )
    },
    [variant],
  )

  return (
    <div className="relative flex size-full flex-row">
      <TemplateFieldAnchor path={variant.path} />
      <TemplateDocumentationTooltip
        tip={
          <Card>
            <CardBody
              outerClassName="min-w-250 max-w-(--breakpoint-2xl)"
              innerClassName="grid grid-cols-[auto_1fr] items-start gap-x-10 gap-y-6"
            >
              <SubTemplatedFieldValueDocumentation
                variant={variant}
                isExtendedView
                areFieldsShownAsEditable={false}
              />
            </CardBody>
          </Card>
        }
        className="h-full grow"
      >
        <div className="relative flex size-full grow items-center justify-between overflow-hidden">
          <SubTemplateFieldValueName variant={variant} />
          <InheritanceIndicator value={variant} />
        </div>
      </TemplateDocumentationTooltip>
      <div className="mx-2 w-10">
        <ValueSetupDiffIndicators
          comparableValues={comparableField}
          serialiseValueFn={valueFromComparable}
        />
      </div>
    </div>
  )
}
